"use client";

import { userApi } from "@/shared/api/api";
import { WISHLIST_API_PATHS } from "@/shared/constants/clientApiPaths";

export interface WishlistItemApi {
  id: string;
  wishlist_id: string;
  product_id: string;
  notes: string | null;
  added_at: string;
  product_name: string;
  slug: string;
  price: string;
  compare_at_price: string;
  primary_image: string;
  brand_name: string;
  rating?: number;
  review_count?: number;
  stock: number;
  out_of_stock: boolean;
  /** Rupees earned on this product, already computed server-side. `null`/absent = no chip. */
  cashback?: number | null;
  /** When true, wishlist cards mask the selling price and hide the discount percentage. */
  hide_coupon_section?: boolean;
  hideCouponSection?: boolean;
}

export interface WishlistGetData {
  wishlist_id: string;
  total_items: number;
  items: WishlistItemApi[];
}

export interface WishlistAddItemData {
  id: string;
  wishlist_id: string;
  product_id: string;
  notes: string | null;
}

export interface WishlistRemoveData {
  success?: boolean;
  message?: string;
}

export interface WishlistMergeData {
  message: string;
  merged_count: number;
}

export interface WishlistItemUi {
  id: string;
  slug: string;
  title: string;
  price: number;
  compare_at_price: number;
  primary_image: string;
  brand_name?: string;
  rating?: number;
  review_count?: number;
  stock?: number;
  out_of_stock?: boolean;
  /** Rupees earned on this product, already computed server-side. `null`/absent = no chip. */
  cashback?: number | null;
  /** When true, wishlist cards mask the selling price and hide the discount percentage. */
  hide_coupon_section?: boolean;
}

export function mapWishlistItemApiToUi(item: WishlistItemApi): WishlistItemUi {
  return {
    id: item.product_id,
    slug: item.slug,
    title: item.product_name,
    price: parseFloat(item.price) || 0,
    compare_at_price: parseFloat(item.compare_at_price) || 0,
    primary_image: item.primary_image,
    brand_name: item.brand_name ?? undefined,
    rating: item.rating,
    review_count: item.review_count,
    stock: item.stock,
    out_of_stock: item.out_of_stock,
    cashback: item.cashback ?? null,
    hide_coupon_section: hideCouponSectionFromWishlistApi(item),
  };
}

/** `undefined` when the payload omits the flag, so a local add can keep its own value. */
function hideCouponSectionFromWishlistApi(item: WishlistItemApi): boolean | undefined {
  if (item.hide_coupon_section === true || item.hideCouponSection === true) return true;
  if (item.hide_coupon_section === false || item.hideCouponSection === false) return false;
  return undefined;
}

export async function fetchWishlist(): Promise<WishlistGetData> {
  return userApi.get<WishlistGetData>(WISHLIST_API_PATHS.list);
}

export async function addToWishlist(productId: string): Promise<WishlistAddItemData> {
  return userApi.post<WishlistAddItemData>(WISHLIST_API_PATHS.items, {
    product_id: productId,
  });
}

export async function removeFromWishlist(productId: string): Promise<WishlistRemoveData> {
  return userApi.delete<WishlistRemoveData>(WISHLIST_API_PATHS.item(productId));
}

export async function mergeWishlist(productIds: string[]): Promise<WishlistMergeData> {
  return userApi.post<WishlistMergeData>(WISHLIST_API_PATHS.merge, {
    product_ids: productIds,
  });
}
